/**
 * Parsers for camera/depth parameters embedded as comments in PLY-derived
 * SpatialData (e.g. "fx: 1000px", "Baseline: 50mm", "rgb24Scale=1000").
 */
export function isDepthDerivedFile(data) {
    const comments = data?.comments;
    if (!Array.isArray(comments)) {
        return false;
    }
    return comments.some((comment) => {
        if (typeof comment !== 'string') {
            return false;
        }
        const lc = comment.toLowerCase();
        return (lc.includes('converted from tif depth image') ||
            lc.includes('converted from pfm depth image') ||
            lc.includes('converted from png depth image') ||
            lc.includes('converted from npy depth image') ||
            lc.includes('converted from depth image'));
    });
}
export function isPngDerivedFile(data) {
    const comments = data?.comments;
    if (!Array.isArray(comments)) {
        return false;
    }
    return comments.some((comment) => typeof comment === 'string' && comment.includes('Converted from PNG depth image'));
}
export function isRgbDerivedFile(data) {
    const comments = data?.comments;
    if (!Array.isArray(comments)) {
        return false;
    }
    return comments.some((comment) => typeof comment === 'string' && comment.includes('RGB24 depth image'));
}
export function getRgb24ScaleFactor(data) {
    const comments = data?.comments;
    if (!Array.isArray(comments)) {
        return 1000;
    }
    for (const comment of comments) {
        if (typeof comment === 'string' && comment.includes('rgb24Scale=')) {
            const match = comment.match(/rgb24Scale=(\d+(?:\.\d+)?)/);
            if (match) {
                return parseFloat(match[1]);
            }
        }
    }
    return 1000; // Default to millimeters
}
export function getRgb24ConversionMode(data) {
    const comments = data?.comments;
    if (!Array.isArray(comments)) {
        return 'shift';
    }
    for (const comment of comments) {
        if (typeof comment === 'string' && comment.includes('rgb24Mode=')) {
            const match = comment.match(/rgb24Mode=(shift|multiply|red|green|blue)/);
            if (match) {
                return match[1];
            }
        }
    }
    return 'shift'; // Default to standard shift mode
}
export function getPngScaleFactor(data) {
    const comments = data?.comments;
    if (!Array.isArray(comments)) {
        return 1000;
    } // Default
    for (const comment of comments) {
        if (typeof comment === 'string' && comment.includes('scale=')) {
            const match = comment.match(/scale=(\d+(?:\.\d+)?)/);
            if (match) {
                return parseFloat(match[1]);
            }
        }
    }
    return 1000; // Default to millimeters
}
export function getDepthSetting(host, data, setting) {
    const comments = data?.comments;
    if (!Array.isArray(comments)) {
        if (setting === 'camera') {
            return host.defaultDepthSettings.cameraModel;
        }
        if (setting === 'depth') {
            return host.defaultDepthSettings.depthType;
        }
        return '';
    }
    for (const comment of comments) {
        if (setting === 'camera' && comment.startsWith('Camera: ')) {
            return comment.replace('Camera: ', '').toLowerCase();
        }
        if (setting === 'depth' && comment.startsWith('Depth: ')) {
            return comment.replace('Depth: ', '').toLowerCase();
        }
    }
    // Return default settings if no setting found in comments
    if (setting === 'camera') {
        return host.defaultDepthSettings.cameraModel;
    }
    if (setting === 'depth') {
        return host.defaultDepthSettings.depthType;
    }
    return '';
}
export function getDepthFx(host, data) {
    const comments = data?.comments;
    if (!Array.isArray(comments)) {
        return host.defaultDepthSettings.fx;
    }
    for (const comment of comments) {
        if (comment.startsWith('fx: ')) {
            const match = comment.match(/(\d+(?:\.\d+)?)px/);
            return match ? parseFloat(match[1]) : host.defaultDepthSettings.fx;
        }
        // Legacy support for 'Focal length:' format
        if (comment.startsWith('Focal length: ')) {
            const match = comment.match(/(\d+(?:\.\d+)?)px/);
            return match ? parseFloat(match[1]) : host.defaultDepthSettings.fx;
        }
    }
    return host.defaultDepthSettings.fx;
}
export function getDepthFy(host, data) {
    const comments = data?.comments;
    if (!Array.isArray(comments)) {
        return host.defaultDepthSettings.fy?.toString() || '';
    }
    for (const comment of comments) {
        if (comment.startsWith('fy: ')) {
            const match = comment.match(/(\d+(?:\.\d+)?)px/);
            return match ? match[1] : host.defaultDepthSettings.fy?.toString() || '';
        }
    }
    return host.defaultDepthSettings.fy?.toString() || '';
}
export function getDepthBaseline(host, data) {
    const comments = data?.comments;
    if (!Array.isArray(comments)) {
        return host.defaultDepthSettings.baseline || 50;
    }
    for (const comment of comments) {
        if (comment.startsWith('Baseline: ')) {
            const match = comment.match(/(\d+(?:\.\d+)?)mm/);
            return match ? parseFloat(match[1]) : host.defaultDepthSettings.baseline || 50;
        }
    }
    return host.defaultDepthSettings.baseline || 50; // Use default baseline
}
export function getDepthCx(host, data, fileIndex) {
    // First try to get dimensions from stored depth data using file index
    if (fileIndex !== undefined) {
        const depthData = host.fileDepthData.get(fileIndex);
        if (depthData?.depthDimensions?.width) {
            const cx = (depthData.depthDimensions.width - 1) / 2;
            return cx.toString();
        }
    }
    // Fall back to checking dimensions on the data object (legacy)
    const dimensions = data?.depthDimensions;
    if (dimensions && dimensions.width) {
        const cx = (dimensions.width - 1) / 2;
        return cx.toString();
    }
    // Return empty string when dimensions aren't available yet (will be auto-calculated)
    return ''; // Empty = will be auto-calculated once image is processed
}
export function getDepthCy(host, data, fileIndex) {
    // First try to get dimensions from stored depth data using file index
    if (fileIndex !== undefined) {
        const depthData = host.fileDepthData.get(fileIndex);
        if (depthData?.depthDimensions?.height) {
            const cy = (depthData.depthDimensions.height - 1) / 2;
            return cy.toString();
        }
    }
    // Fall back to checking dimensions on the data object (legacy)
    const dimensions = data?.depthDimensions;
    if (dimensions && dimensions.height) {
        const cy = (dimensions.height - 1) / 2;
        return cy.toString();
    }
    // Return empty string when dimensions aren't available yet (will be auto-calculated)
    return ''; // Empty = will be auto-calculated once image is processed
}
export function getDepthConvention(host, data) {
    // Check if this file was processed with a specific convention
    const comments = data?.comments;
    if (Array.isArray(comments)) {
        for (const comment of comments) {
            if (comment.includes('Convention: ')) {
                const convention = comment.replace('Convention: ', '').toLowerCase();
                if (convention === 'opencv' || convention === 'opengl') {
                    return convention;
                }
            }
        }
    }
    // Use default convention from settings
    return host.defaultDepthSettings.convention || 'opengl';
}
